import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

export const WorkflowTemplates = {
  'simple-pipeline': {
    name: 'Simple Pipeline',
    description: 'Basic sequential workflow for straightforward processes',
    phases: [
      { 
        id: 'brainstorm',
        type: 'brainstorming', 
        label: 'Brainstorm Ideas',
        position: { x: 100, y: 100 },
        config: { tools: ['brainstorming'], personas: [] }
      },
      { 
        id: 'critique',
        type: 'critique', 
        label: 'Review & Critique',
        position: { x: 400, y: 100 },
        config: { tools: ['critique'], personas: [] }
      },
      { 
        id: 'refine',
        type: 'refinement', 
        label: 'Refine Solution',
        position: { x: 700, y: 100 },
        config: { tools: ['refinement'], personas: [] }
      }
    ],
    connections: [
      { source: 'brainstorm', target: 'critique', sourceHandle: 'output', targetHandle: 'input', dependencyType: 'flow' },
      { source: 'critique', target: 'refine', sourceHandle: 'output', targetHandle: 'input', dependencyType: 'flow' }
    ]
  },
  
  'committee-review': {
    name: 'Committee Review',
    description: 'Multi-persona collaborative workflow with parallel processing',
    phases: [
      { 
        id: 'team-select',
        type: 'team_selection', 
        label: 'Select Team',
        position: { x: 100, y: 150 },
        config: { tools: ['team_selection'], personas: [] }
      },
      { 
        id: 'parallel-brainstorm',
        type: 'brainstorming', 
        label: 'Parallel Brainstorming',
        position: { x: 400, y: 50 },
        config: { tools: ['brainstorming'], personas: [] }
      },
      { 
        id: 'parallel-research',
        type: 'brainstorming', 
        label: 'Research Phase',
        position: { x: 400, y: 250 },
        config: { tools: ['web_search'], personas: [] }
      },
      { 
        id: 'synthesis',
        type: 'synthesis', 
        label: 'Synthesize Results',
        position: { x: 700, y: 150 },
        config: { tools: ['synthesis'], personas: [] }
      }
    ],
    connections: [
      { source: 'team-select', target: 'parallel-brainstorm', sourceHandle: 'output', targetHandle: 'input', dependencyType: 'flow' },
      { source: 'team-select', target: 'parallel-research', sourceHandle: 'output', targetHandle: 'input', dependencyType: 'flow' },
      { source: 'parallel-brainstorm', target: 'synthesis', sourceHandle: 'output', targetHandle: 'input', dependencyType: 'flow' },
      { source: 'parallel-research', target: 'synthesis', sourceHandle: 'output', targetHandle: 'input', dependencyType: 'flow' }
    ]
  },

  'research-analysis': {
    name: 'Research & Analysis',
    description: 'Deep research workflow with analysis and validation',
    phases: [
      { 
        id: 'research-planning',
        type: 'team_selection', 
        label: 'Research Planning',
        position: { x: 100, y: 100 },
        config: { tools: ['team_selection'], personas: [] }
      },
      { 
        id: 'data-gathering',
        type: 'brainstorming', 
        label: 'Data Gathering',
        position: { x: 400, y: 100 },
        config: { tools: ['web_search', 'document_analysis'], personas: [] }
      },
      { 
        id: 'analysis',
        type: 'critique', 
        label: 'Analysis & Insights',
        position: { x: 700, y: 100 },
        config: { tools: ['critique'], personas: [] }
      },
      { 
        id: 'validation',
        type: 'refinement', 
        label: 'Validate Findings',
        position: { x: 1000, y: 100 },
        config: { tools: ['refinement'], personas: [] }
      }
    ],
    connections: [
      { source: 'research-planning', target: 'data-gathering', sourceHandle: 'output', targetHandle: 'input', dependencyType: 'flow' },
      { source: 'data-gathering', target: 'analysis', sourceHandle: 'output', targetHandle: 'input', dependencyType: 'flow' },
      { source: 'analysis', target: 'validation', sourceHandle: 'output', targetHandle: 'input', dependencyType: 'flow' }
    ]
  }
};

interface TemplateSelectorProps {
  onSelectTemplate: (templateKey: string) => void;
  onClose: () => void;
}

export const TemplateSelector: React.FC<TemplateSelectorProps> = ({ onSelectTemplate, onClose }) => {
  const { darkMode } = useTheme();
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] overflow-y-auto ${darkMode ? 'bg-gray-800' : 'bg-white'} p-6`}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold">Choose Workflow Template</h3>
          <button
            onClick={onClose}
            className={`p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700`}
          >
            ✕
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(WorkflowTemplates).map(([key, template]) => (
            <div
              key={key}
              className={`p-6 border-2 rounded-xl cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105 ${
                darkMode 
                  ? 'border-gray-600 hover:border-blue-500 bg-gray-700' 
                  : 'border-gray-200 hover:border-blue-400 bg-white hover:bg-blue-50'
              }`}
              onClick={() => onSelectTemplate(key)}
            >
              <h4 className="font-bold text-lg mb-3">{template.name}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                {template.description}
              </p>
              
              {/* Visual preview */}
              <div className="mb-4">
                <div className="flex items-center space-x-2 mb-2">
                  {template.phases.slice(0, 3).map((phase, index) => (
                    <React.Fragment key={phase.id}>
                      <div className={`w-8 h-6 rounded text-xs flex items-center justify-center ${
                        darkMode ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {index + 1}
                      </div>
                      {index < Math.min(template.phases.length - 1, 2) && (
                        <div className="w-4 h-0.5 bg-gray-400"></div>
                      )}
                    </React.Fragment>
                  ))}
                  {template.phases.length > 3 && (
                    <span className="text-xs text-gray-500">+{template.phases.length - 3}</span>
                  )}
                </div>
              </div>
              
              <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400">
                <span>{template.phases.length} phases</span>
                <span>{template.connections.length} connections</span>
              </div>
              
              <div className={`mt-3 px-3 py-1 rounded-full text-xs font-medium text-center ${
                darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
              }`}>
                Use Template
              </div>
            </div>
          ))}
          
          {/* Blank template option */}
          <div
            className={`p-6 border-2 border-dashed rounded-xl cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105 ${
              darkMode 
                ? 'border-gray-600 hover:border-green-500 bg-gray-700' 
                : 'border-gray-300 hover:border-green-400 bg-gray-50 hover:bg-green-50'
            }`}
            onClick={() => onSelectTemplate('blank')}
          >
            <div className="text-center">
              <div className="text-4xl mb-3">✨</div>
              <h4 className="font-bold text-lg mb-3">Start Blank</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                Create your workflow from scratch with complete creative freedom
              </p>
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                darkMode ? 'bg-green-600 text-white' : 'bg-green-500 text-white'
              }`}>
                Start Fresh
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
